/**
 * Form state for the Organisation Structure node edit dialog.
 *
 * Extracted from NodeConfigPanel so the panel stays inside the 500-line limit
 * once CR-6247 added the expanded company field set. Holds only state and the
 * populate-from-node effect — the save calls stay in the panel, where the
 * per-node-type API routing lives.
 */
import { useState, useEffect } from 'react';
import type { OrgChartNode } from './types';

/** Platform reporting currency — the sensible default for a new site. */
const DEFAULT_BASE_CURRENCY = 'GBP';

/** Every editable value the dialog manages, as strings the inputs bind to. */
export interface NodeConfigForm {
  name: string;
  code: string;
  description: string;
  city: string;
  country: string;
  website: string;
  primaryEmail: string;
  isActive: boolean;
  // CR-6691: full structured address for companies
  addressLine1: string;
  addressLine2: string;
  stateCounty: string;
  postalCode: string;
  // CR-6247: expanded company fields
  registrationNumber: string;
  employeeCount: string;
  yearEstablished: string;
  annualRevenue: string;
  currency: string;
}

const EMPTY_FORM: NodeConfigForm = {
  name: '',
  code: '',
  description: '',
  city: '',
  country: '',
  website: '',
  primaryEmail: '',
  isActive: true,
  addressLine1: '',
  addressLine2: '',
  stateCounty: '',
  postalCode: '',
  registrationNumber: '',
  employeeCount: '',
  yearEstablished: '',
  annualRevenue: '',
  currency: '',
};

/** Numbers arrive as numbers or null; the inputs need strings. */
const asText = (value: string | number | null | undefined): string =>
  value === null || value === undefined ? '' : String(value);

export interface UseNodeConfigFormResult {
  form: NodeConfigForm;
  /** Update one field. */
  setField: <K extends keyof NodeConfigForm>(field: K, value: NodeConfigForm[K]) => void;
}

/**
 * Manage the dialog's form state, re-seeding whenever a different node opens.
 *
 * @param node - the node being created or edited, or null when closed
 * @returns the current form values plus a single-field setter
 */
export function useNodeConfigForm(node: OrgChartNode | null): UseNodeConfigFormResult {
  const [form, setForm] = useState<NodeConfigForm>(EMPTY_FORM);

  useEffect(() => {
    if (!node) return;
    const meta = node.metadata;
    setForm({
      name: node.name || '',
      code: node.code || '',
      description: node.description || '',
      city: meta?.city || '',
      country: meta?.country || meta?.headquarters_country || '',
      website: meta?.website || '',
      primaryEmail: meta?.primary_email || '',
      isActive: node.is_active,
      addressLine1: meta?.address_line_1 || '',
      addressLine2: meta?.address_line_2 || '',
      stateCounty: meta?.state || '',
      postalCode: meta?.postal_code || '',
      registrationNumber: meta?.registration_number || '',
      employeeCount: asText(meta?.employee_count),
      yearEstablished: asText(meta?.year_established),
      annualRevenue: asText(meta?.annual_revenue),
      // A site that inherits has a dormant own-currency, so seed from the
      // resolved value — otherwise the dropdown would read as "unset" for
      // every inheriting site and a save would look like a change. A brand new
      // site has neither, so it starts on the platform default rather than
      // needing a second trip to Admin Setup > Regional.
      currency:
        node.effective_settings?.currency?.value
        || node.regional_settings?.currency
        || (node.entity_id === 0 ? DEFAULT_BASE_CURRENCY : ''),
    });
  }, [node]);

  const setField = <K extends keyof NodeConfigForm>(field: K, value: NodeConfigForm[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  return { form, setField };
}
